import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import {
  DEFAULT_FONT_SCALE,
  applyFontScale,
  isFontScale,
  type FontScale,
} from '@/lib/fontScale';

interface FontScaleContextValue {
  /** 現在の文字サイズ設定（未設定なら標準） */
  scale: FontScale;
  /** 設定済みかどうか（未設定なら初回ポップアップを出す） */
  configured: boolean;
  saving: boolean;
  error: string | null;
  /** 設定を保存して画面へ反映する。成功したら true。 */
  setScale: (scale: FontScale) => Promise<boolean>;
}

const FontScaleContext = createContext<FontScaleContextValue | undefined>(undefined);

/**
 * ユーザーごとの文字サイズ設定を管理する Provider。
 * - ログイン中ユーザーの profile.font_scale を読み、ルート要素の font-size に反映する
 *   （＝画面全体をブラウザのズームに近い形で一括で拡大/縮小）。
 * - 保存は本人の profiles 行に対して行う（RLS: id = auth.uid() で許可済み）。
 */
export function FontScaleProvider({ children }: { children: ReactNode }) {
  const { user, profile, refreshProfile } = useAuth();
  const rawScale = (profile as { font_scale?: string | null } | null)?.font_scale ?? null;
  const configured = isFontScale(rawScale);
  const scale: FontScale = configured ? rawScale : DEFAULT_FONT_SCALE;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 設定が変わるたびに画面全体の文字サイズを適用する
  useEffect(() => {
    applyFontScale(scale);
  }, [scale]);

  const setScale = useCallback(
    async (next: FontScale): Promise<boolean> => {
      // まず即時反映（体感を良くする）
      applyFontScale(next);
      if (!user) return false;
      setSaving(true);
      setError(null);
      try {
        const { error: updateError } = await supabase
          .from('profiles')
          .update({ font_scale: next })
          .eq('id', user.id);
        if (updateError) throw updateError;
        await refreshProfile();
        return true;
      } catch (e) {
        console.error('文字サイズ設定の保存に失敗:', e);
        setError('文字サイズを保存できませんでした。通信環境を確認してもう一度お試しください。');
        // 失敗したら元の設定へ戻す
        applyFontScale(scale);
        return false;
      } finally {
        setSaving(false);
      }
    },
    [user, refreshProfile, scale]
  );

  const value = useMemo(
    () => ({ scale, configured, saving, error, setScale }),
    [scale, configured, saving, error, setScale]
  );

  return <FontScaleContext.Provider value={value}>{children}</FontScaleContext.Provider>;
}

export function useFontScale() {
  const context = useContext(FontScaleContext);
  if (context === undefined) {
    throw new Error('useFontScale must be used within a FontScaleProvider');
  }
  return context;
}